/**
 * Size caps applied to anything a client can send.
 *
 * Every student route is anonymous by design, so these bounds are the only
 * thing standing between an open endpoint and unbounded storage growth.
 */

/** Roughly a 40,000-word essay with formatting markup. */
export const MAX_DRAFT_HTML_CHARS = 400_000;
export const MAX_DRAFT_TEXT_CHARS = 200_000;
/** The assignment prompt is reference material, not the submission. */
export const MAX_REFERENCE_PROMPT_CHARS = 20_000;
/** Longest professor email we accept, well above any real Sinclair address. */
export const MAX_PROFESSOR_EMAIL_CHARS = 254;
/** General feedback is free text, but still bounded like every other input. */
export const MAX_FEEDBACK_MESSAGE_CHARS = 4_000;
export const MAX_FEEDBACK_NAME_CHARS = 100;
/** The store keeps only the newest feedback entries. */
export const MAX_FEEDBACK_ENTRIES = 500;
/** Caps both the user-agent string and the rest of the attached context. */
export const MAX_FEEDBACK_CONTEXT_CHARS = 500;
/** One entry per 180-character burst; 500 covers a very long session. */
export const MAX_MAJOR_GROWTH_EVENTS = 500;
/**
 * Hard ceiling for every client-supplied metric counter: word/character/key
 * counts, event counts, and session-time milliseconds. Real values sit many
 * orders of magnitude below this — a 40,000-word essay is under 1e5 words, a
 * multi-day session is under 1e9 ms, and even a pathological keystroke total
 * across that time is under 1e7 — so 1e12 can never clip a legitimate value.
 * It does keep `formatCount`/`toLocaleString` output short enough to render on
 * a single PDF line, which is why an unbounded `1e300` was a display hazard.
 * Values above the cap are clamped, not rejected, so a tampered or legacy
 * value is repaired on read (see `normalizeMetrics`).
 */
export const MAX_METRIC_VALUE = 1e12;
/**
 * Bytes accepted in a single request body. Sized above the worst-case schema
 * caps: the web client syncs a draft's html and text together, so a 600,000
 * character payload (400,000 html + 200,000 text) at four bytes per CJK/emoji
 * character serializes to 2.4 MB before the JSON envelope and event batch are
 * added — a 2 MB bound would 413 a legitimate max-length CJK draft before
 * validation ever ran.
 */
export const MAX_REQUEST_BODY_BYTES = 2_500_000;
